import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listAllParametros } from '@/shared/api/controllers/parametros';
import { listAllParametrosComDesc, parseStoredValue } from '@/shared/api/controllers/parametrosComDesc';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  createContacto,
  updateContacto,
  type ContactoItem,
} from '@/shared/api/controllers/contactos';
import {
  PAISES_EXPORTACION_QUERY_KEY,
  TIPOS_ENTIDAD_EXTERIOR,
  listPaisesExportacion,
} from '@/shared/api/controllers/catalogos';
import { contactSchema, emptyContact, type ContactFormValues } from './schema';
import { formToFields, itemToForm } from './mappers';
import {
  CONDICIONES_IVA_CONTACTO,
  CONDICION_CLIENTE_EXTERIOR,
  ESTADOS_MX,
  MEDIOS_DE_LLEGADA,
  PROVINCIAS_ARGENTINA,
  USOS_CFDI,
} from './constants';
import { CONTACTOS_QUERY_KEY } from './queries';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent = create. Present = edit. */
  contact?: ContactoItem | null;
  /** Used for the duplicate-name check on create. */
  existingContacts: ContactoItem[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export function ContactDialog({
  open,
  onOpenChange,
  contact,
  existingContacts,
  onSuccess,
  onError,
}: Props) {
  const queryClient = useQueryClient();
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  // Country drives the tax-id label (CUIT on AR, RFC on MX), the
  // Provincia/Estado dropdown options, and whether the IVA condition
  // is a fixed dropdown (AR has the SP-derived CONDICIONES_IVA list)
  // or a free-text field (MX uses SAT regime names that we don't
  // hardcode here yet).
  const isMx = activeCountry.code === 'MX';
  // % de E-cheq para despacho: política de crédito, sólo admin/administración (AR).
  const editaPctEcheq =
    !isMx && (profile?.perfil === 'admin' || profile?.perfil === 'administracion');
  // Comisión por cliente: mismo alcance (el server también se la rechaza a ventas).
  const editaComision = editaPctEcheq;
  // Tasa general (misma fila que usa el reporte): de acá arranca el campo.
  const parametrosComDescQ = useQuery({
    queryKey: ['contactos', 'parametros-com-desc', activeCountry.code],
    queryFn: listAllParametrosComDesc,
    enabled: editaComision,
    staleTime: 5 * 60_000,
  });
  const tasaGeneral = parametrosComDescQ.data?.find(
    (p) =>
      p.fields.Parametro_PCD === 'Comisiones de Vendedores' &&
      p.fields.Tipo_PCD === 'Mensual' &&
      p.fields.Status_PCD === 'Activo',
  )?.fields.ValorPorcentaje_PCD;
  const comisionGeneral =
    tasaGeneral != null
      ? String(Number((parseStoredValue(tasaGeneral) * 100).toFixed(2))).replace('.', ',')
      : '';
  // Valor general (ABM Parámetros) para mostrar qué rige si el campo queda vacío.
  // Misma clave que el ABM: si lo editan ahí, acá se ve el nuevo.
  const parametrosQ = useQuery({
    queryKey: ['configuracion', 'parametros-sistema', activeCountry.code],
    queryFn: listAllParametros,
    enabled: editaPctEcheq,
    staleTime: 5 * 60_000,
  });
  const pctGeneral = parametrosQ.data?.find(
    (p) => p.fields.NombreParametro_P === 'Porcentaje Minimo E-cheq Despacho',
  )?.fields.Valor_P;
  const pctGeneralTexto = pctGeneral != null && String(pctGeneral).trim() !== '' ? `${pctGeneral}%` : null;
  const taxIdLabel = isMx ? 'RFC' : 'CUIT';
  const taxIdPlaceholder = isMx ? 'FNF250101AB9' : '20-12345678-9';
  const provinciaLabel = isMx ? 'Estado' : 'Provincia';
  const provinciaOptions: readonly string[] = isMx ? ESTADOS_MX : PROVINCIAS_ARGENTINA;
  const provinciaPlaceholder = isMx ? 'Seleccionar Estado' : 'Seleccionar Provincia';
  const mode: 'create' | 'edit' = contact ? 'edit' : 'create';

  const defaults = useMemo<ContactFormValues>(
    () => (contact ? itemToForm(contact) : emptyContact),
    [contact],
  );

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: defaults,
  });

  // Reset form when the dialog opens with a different contact.
  useEffect(() => {
    if (open) form.reset(defaults);
  }, [open, defaults, form]);

  // Cliente del Exterior (Factura E): pide país / tipo de entidad / ID impositivo en lugar de CUIT.
  const esExterior = !isMx && form.watch('condicionIVA') === CONDICION_CLIENTE_EXTERIOR;
  const paisesQ = useQuery({
    queryKey: PAISES_EXPORTACION_QUERY_KEY,
    queryFn: listPaisesExportacion,
    enabled: esExterior,
    staleTime: Infinity,
  });
  const paises = paisesQ.data ?? [];
  const paisSel = paises.find((p) => p.codigo === form.watch('paisDestinoArca'));
  const tiposEntidad = TIPOS_ENTIDAD_EXTERIOR.filter(
    (t) => !paisSel || paisSel.tipos.includes(t.value),
  );

  const medioDeLlegada = form.watch('medioDeLlegada');
  const requiresReferido = medioDeLlegada === 'Referidos';
  const comisionPropia = form.watch('comisionPropia');

  const watchedNombre = form.watch('nombre');
  const watchedApellido = form.watch('apellido');
  const watchedCelular = form.watch('celular');
  const watchedReferido = form.watch('referido');

  const watchedPaisExterior = form.watch('paisDestinoArca');
  const watchedTipoExterior = form.watch('tipoEntidadExterior');
  const watchedIdExterior = form.watch('idImpositivoExterior');

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedNombre?.trim()) missing.push('Nombre');
    if (!watchedApellido?.trim()) missing.push('Apellido');
    if (!watchedCelular?.trim()) missing.push('Celular');
    // Cómo llegó a nosotros is now optional (QA request); only Referido
    // becomes required when the user explicitly picks 'Referidos'.
    if (requiresReferido && !watchedReferido?.trim()) missing.push('Referido');
    // Cliente del Exterior: para la Factura E hacen falta país, tipo de entidad e ID impositivo.
    if (esExterior) {
      if (!watchedPaisExterior?.trim()) missing.push('País de destino');
      if (!watchedTipoExterior?.trim()) missing.push('Tipo de entidad');
      if (!watchedIdExterior?.trim()) missing.push('ID impositivo');
    }
    return missing;
  }, [
    watchedNombre,
    watchedApellido,
    watchedCelular,
    requiresReferido,
    watchedReferido,
    esExterior,
    watchedPaisExterior,
    watchedTipoExterior,
    watchedIdExterior,
  ]);

  const canSubmit = missingFields.length === 0;

  const mutation = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      const fields = formToFields(values, { conPctEcheq: editaPctEcheq, conComision: editaComision });
      if (mode === 'edit' && contact) {
        return updateContacto(contact.id, fields);
      }
      return createContacto(fields, profile?.usuario_app ?? '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTACTOS_QUERY_KEY });
      onOpenChange(false);
      onSuccess(
        mode === 'edit'
          ? 'El contacto se actualizó correctamente.'
          : 'El contacto se creó correctamente.',
      );
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'No se pudo guardar el contacto.';
      onOpenChange(false);
      onError(message);
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    const normalized = (s: string) => s.trim().toLowerCase();
    const duplicated = existingContacts.find(
      (c) =>
        c.id !== contact?.id &&
        normalized(c.fields.Nombre_C ?? '') === normalized(values.nombre) &&
        normalized(c.fields.Apellido_C ?? '') === normalized(values.apellido),
    );
    if (duplicated) {
      form.setError('apellido', {
        type: 'manual',
        message: 'Ya existe un contacto con ese nombre y apellido.',
      });
      return;
    }
    mutation.mutate(values);
  });

  const fieldError = (name: keyof ContactFormValues) =>
    form.formState.errors[name]?.message as string | undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-5xl"
        processing={mutation.isPending && (mode === 'edit' ? 'Guardando cambios…' : 'Creando contacto…')}
      >
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Editar contacto' : 'Añadir contacto'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Modificá los datos del contacto y guardá los cambios.'
              : 'Completá los datos para dar de alta un nuevo contacto.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          {/* Campos más bajos que el default: el formulario entra casi sin scroll. */}
          <DialogBody className="space-y-3 [&_input]:h-9 [&_label]:text-xs [&_button[role=combobox]]:h-9">
            <Grupo titulo="Contacto">
              <Grid>
                <Field label="Nombre" htmlFor="nombre" required error={fieldError('nombre')}>
                  <Input
                    id="nombre"
                    variant="filled"
                    placeholder="Ingresar Nombre"
                    {...form.register('nombre')}
                  />
                </Field>
                <Field label="Apellido" htmlFor="apellido" required error={fieldError('apellido')}>
                  <Input
                    id="apellido"
                    variant="filled"
                    placeholder="Ingresar Apellido"
                    {...form.register('apellido')}
                  />
                </Field>
                <Field label="Mail" htmlFor="mail" error={fieldError('mail')}>
                  <Input
                    id="mail"
                    type="email"
                    variant="filled"
                    placeholder="Ingresar Mail"
                    {...form.register('mail')}
                  />
                </Field>
              </Grid>

              <Grid>
                <Field label="Celular" htmlFor="celular" required error={fieldError('celular')}>
                  <Input
                    id="celular"
                    type="tel"
                    inputMode="tel"
                    variant="filled"
                    placeholder="Ingresar Número de Celular"
                    {...form.register('celular')}
                  />
                </Field>
                <Field label="Cómo llegó a nosotros" error={fieldError('medioDeLlegada')}>
                  <Select
                    value={medioDeLlegada ?? ''}
                    onValueChange={(v) =>
                      form.setValue('medioDeLlegada', v as ContactFormValues['medioDeLlegada'], {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar Medio" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEDIOS_DE_LLEGADA.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field
                  label="Referido"
                  htmlFor="referido"
                  required={requiresReferido}
                  error={fieldError('referido')}
                >
                  <Input
                    id="referido"
                    variant="filled"
                    placeholder={requiresReferido ? 'Ingresar referido' : '—'}
                    disabled={!requiresReferido}
                    {...form.register('referido')}
                  />
                </Field>
              </Grid>
            </Grupo>

            <Grupo titulo="Facturación">
              {/* Single 12-col row holding RFC/CUIT, Razón Social, Régimen
                  Fiscal / Condición IVA, and (MX only) Código Postal +
                  Uso del CFDI. Tighter than two stacked 3-col grids: the
                  narrow tax fields take 2 cols each so they don't waste
                  width, and Razón Social keeps the 4 cols it needs. */}
              <div className="grid gap-3 md:grid-cols-12">
                <div className={isMx ? 'md:col-span-2' : 'md:col-span-4'}>
                  {esExterior ? (
                    // Un cliente del exterior no tiene CUIT argentino: se factura con su número
                    // fiscal en el país de destino (RUT, RFC, EIN…). Va a ARCA como Id_impositivo.
                    <Field
                      label="ID impositivo"
                      htmlFor="idImpositivoExterior"
                      error={fieldError('idImpositivoExterior')}
                    >
                      <Input
                        id="idImpositivoExterior"
                        variant="filled"
                        placeholder="RUT, RFC, EIN…"
                        {...form.register('idImpositivoExterior')}
                      />
                    </Field>
                  ) : (
                    <Field label={taxIdLabel} htmlFor="cuit" error={fieldError('cuit')}>
                      <Input
                        id="cuit"
                        // RFC has letters; CUIT is digits-only. `inputMode`
                        // hints the soft keyboard accordingly so MX users
                        // don't get a numeric keypad on a letter-bearing
                        // tax id.
                        inputMode={isMx ? 'text' : 'numeric'}
                        variant="filled"
                        placeholder={taxIdPlaceholder}
                        {...form.register('cuit')}
                      />
                    </Field>
                  )}
                </div>
                <div className="md:col-span-4">
                  <Field label="Razón Social" htmlFor="razonSocial" error={fieldError('razonSocial')}>
                    <Input
                      id="razonSocial"
                      variant="filled"
                      placeholder="Ingresar Razón Social"
                      {...form.register('razonSocial')}
                    />
                  </Field>
                </div>
                <div className={isMx ? 'md:col-span-2' : 'md:col-span-4'}>
                  <Field
                    label={isMx ? 'Régimen Fiscal' : 'Condición IVA'}
                    error={fieldError('condicionIVA')}
                  >
                    {isMx ? (
                      // MX SAT regime list isn't hardcoded yet — let the
                      // operator type whatever the cliente's RFC carries.
                      // Convert to a dropdown later when we have the
                      // catalog.
                      <Input
                        variant="filled"
                        placeholder="Régimen SAT"
                        value={form.watch('condicionIVA') ?? ''}
                        onChange={(e) =>
                          form.setValue('condicionIVA', e.target.value, {
                            shouldDirty: true,
                          })
                        }
                      />
                    ) : (
                      <Select
                        value={form.watch('condicionIVA') ?? ''}
                        onValueChange={(v) =>
                          form.setValue('condicionIVA', v, { shouldDirty: true })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONDICIONES_IVA_CONTACTO.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                </div>
                {/* Cliente del Exterior (AR): país de destino y tipo de entidad. Con los dos ARCA asigna
                    el CUIT-país del receptor; el ID impositivo (arriba) es su número fiscal real. */}
                {esExterior && (
                  <>
                    <div className="md:col-span-4">
                      <Field label="País de destino" error={fieldError('paisDestinoArca')}>
                        <Select
                          value={form.watch('paisDestinoArca') ?? ''}
                          onValueChange={(v) => {
                            form.setValue('paisDestinoArca', v, { shouldDirty: true });
                            // El tipo elegido puede no existir para el país nuevo.
                            const nuevo = paises.find((p) => p.codigo === v);
                            const tipo = form.getValues('tipoEntidadExterior');
                            if (nuevo && tipo && !nuevo.tipos.includes(tipo as 'juridica' | 'fisica' | 'otro')) {
                              form.setValue('tipoEntidadExterior', undefined, { shouldDirty: true });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={paisesQ.isPending ? 'Cargando…' : 'Seleccionar país'}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {paises.map((p) => (
                              <SelectItem key={p.codigo} value={p.codigo}>
                                {p.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <div className="md:col-span-4">
                      <Field label="Tipo de entidad" error={fieldError('tipoEntidadExterior')}>
                        <Select
                          value={form.watch('tipoEntidadExterior') ?? ''}
                          onValueChange={(v) =>
                            form.setValue('tipoEntidadExterior', v, { shouldDirty: true })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            {tiposEntidad.map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  </>
                )}
                {/* MX SAT receptor data — CP + Uso del CFDI. Required by
                    Nova to stamp the CFDI 4.0 factura. Hidden on AR. */}
                {isMx && (
                  <>
                    <div className="md:col-span-2">
                      <Field
                        label="Código Postal"
                        htmlFor="codigoPostal"
                        error={fieldError('codigoPostal')}
                      >
                        <Input
                          id="codigoPostal"
                          inputMode="numeric"
                          variant="filled"
                          placeholder="06600"
                          maxLength={5}
                          {...form.register('codigoPostal')}
                        />
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Uso del CFDI" error={fieldError('usoCfdi')}>
                        <Select
                          value={form.watch('usoCfdi') ?? ''}
                          onValueChange={(v) =>
                            form.setValue('usoCfdi', v, { shouldDirty: true })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Uso CFDI" />
                          </SelectTrigger>
                          <SelectContent>
                            {USOS_CFDI.map((u) => (
                              <SelectItem key={u.code} value={u.code}>
                                {u.code} — {u.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  </>
                )}
              </div>

              {/* Address fits in one row at 5xl: provincia/ciudad/dirección
                  share half, altura/piso/depto sit on the other half. */}
              <div className="grid gap-3 md:grid-cols-12">
                {/* Un cliente del exterior no tiene provincia argentina. */}
                {!esExterior && (
                <div className="md:col-span-3">
                  <Field label={provinciaLabel} error={fieldError('provincia')}>
                    <Select
                      value={form.watch('provincia') ?? ''}
                      onValueChange={(v) =>
                        form.setValue('provincia', v, { shouldDirty: true })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={provinciaPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {provinciaOptions.map((p) => (
                          <SelectItem key={p} value={p}>
                            {p}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                )}
                <div className="md:col-span-2">
                  <Field label="Ciudad" htmlFor="ciudad" error={fieldError('ciudad')}>
                    <Input
                      id="ciudad"
                      variant="filled"
                      placeholder="Ingresar Ciudad"
                      {...form.register('ciudad')}
                    />
                  </Field>
                </div>
                <div className="md:col-span-3">
                  <Field label="Dirección" htmlFor="direccion" error={fieldError('direccion')}>
                    <Input
                      id="direccion"
                      variant="filled"
                      placeholder="Ingresar Dirección"
                      {...form.register('direccion')}
                    />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Altura" htmlFor="altura" error={fieldError('altura')}>
                    <Input
                      id="altura"
                      inputMode="numeric"
                      variant="filled"
                      placeholder="-"
                      {...form.register('altura')}
                    />
                  </Field>
                </div>
                <div className="md:col-span-1">
                  <Field label="Piso" htmlFor="piso">
                    <Input id="piso" variant="filled" placeholder="-" {...form.register('piso')} />
                  </Field>
                </div>
                <div className="md:col-span-1">
                  <Field label="Depto" htmlFor="depto">
                    <Input id="depto" variant="filled" placeholder="-" {...form.register('depto')} />
                  </Field>
                </div>
              </div>
            </Grupo>

            <Grupo titulo="Envío">
              {/* On md+ the three envío fields sit on one row; on mobile
                  the grid collapses naturally so each gets full width. */}
              <div className="grid gap-3 md:grid-cols-12">
                <div className="md:col-span-5">
                  <Field
                    label="Dirección completa"
                    htmlFor="direccionCompleta"
                    error={fieldError('direccionCompleta')}
                  >
                    <Input
                      id="direccionCompleta"
                      variant="filled"
                      placeholder="Ingresar Dirección Completa"
                      {...form.register('direccionCompleta')}
                    />
                  </Field>
                </div>
                <div className="md:col-span-3">
                  <Field label="Recibe" htmlFor="recibe" error={fieldError('recibe')}>
                    <Input
                      id="recibe"
                      variant="filled"
                      placeholder="Ingresar Receptor"
                      {...form.register('recibe')}
                    />
                  </Field>
                </div>
                <div className="md:col-span-4">
                  <Field
                    label="Observaciones"
                    htmlFor="observaciones"
                    error={fieldError('observaciones')}
                  >
                    <Input
                      id="observaciones"
                      variant="filled"
                      placeholder="Ingresar Observaciones"
                      {...form.register('observaciones')}
                    />
                  </Field>
                </div>
              </div>
            </Grupo>

            {editaComision && (
              <Grupo titulo="Cobranza y comisión">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Field
                      label="Cobrado para poder despachar con E-cheqs (%)"
                      htmlFor="pctMinimoEcheq"
                      error={fieldError('pctMinimoEcheq')}
                    >
                      <Input
                        id="pctMinimoEcheq"
                        variant="filled"
                        inputMode="decimal"
                        placeholder={pctGeneralTexto ?? ''}
                        {...form.register('pctMinimoEcheq')}
                      />
                    </Field>
                    <p className="text-xs text-muted-foreground">
                      Cuando la venta se paga con E-cheqs: qué porcentaje del total tiene que estar cobrado
                      (los demás pagos más los E-cheqs ya acreditados) para poder despachar el horno. Si lo
                      dejás vacío, se usa el valor general
                      {pctGeneralTexto ? ` (${pctGeneralTexto})` : ''}.
                    </p>
                  </div>

                  <div className="space-y-1">
                    <Field
                      label="Comisión propia (%)"
                      htmlFor="comisionVendedor"
                      error={fieldError('comisionVendedor')}
                    >
                      <div className="flex items-center gap-3">
                        <Switch
                          aria-label="Comisión propia"
                          checked={comisionPropia}
                          onCheckedChange={(on) => {
                            form.setValue('comisionPropia', on, { shouldDirty: true, shouldValidate: true });
                            if (!on) form.clearErrors('comisionVendedor');
                            if (on && !form.getValues('comisionVendedor')?.trim()) {
                              form.setValue('comisionVendedor', comisionGeneral, { shouldDirty: true });
                            }
                          }}
                        />
                        <Input
                          id="comisionVendedor"
                          variant="filled"
                          inputMode="decimal"
                          disabled={!comisionPropia}
                          placeholder={comisionGeneral}
                          {...form.register('comisionVendedor')}
                        />
                      </div>
                    </Field>
                    <p className="text-xs text-muted-foreground">
                      Si está prendida, este porcentaje reemplaza a la comisión general
                      {comisionGeneral ? ` (${comisionGeneral}%)` : ''} en las ventas a este cliente. El
                      umbral del mes aplica igual.
                    </p>
                  </div>
                </div>
              </Grupo>
            )}
          </DialogBody>

          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {mode === 'edit' ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Subgrupo del formulario: rótulo chico y separador, sin tarjeta. */
function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 border-t pt-2 first:border-t-0 first:pt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</h3>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 md:grid-cols-3">{children}</div>;
}

function Field({
  label,
  htmlFor,
  required,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
